import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataGrid } from './yeni-design/DataGrid';
import { queryDataSource } from '../api/query';
import { mapToDataGridColumns } from './columnUtils';
import type { DetailConfig, ReportConfig } from '../types/panelConfig';

interface DetailPanelProps {
  detailConfig: DetailConfig;
  masterRow: Record<string, unknown> | null;
  allReports: ReportConfig[];
}

type DetailRow = Record<string, unknown>;

export function DetailPanel({ detailConfig, masterRow, allReports }: DetailPanelProps) {
  const detailReport = useMemo(
    () => allReports.find((r) => r.code === detailConfig.detailReportCode),
    [allReports, detailConfig.detailReportCode]
  );

  const columnDefs = useMemo(
    () => (detailReport ? mapToDataGridColumns<DetailRow>(detailReport.columns) : []),
    [detailReport]
  );

  const linkValue = masterRow?.[detailConfig.linkField];
  const sourceId = detailReport?.sourceId ?? null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['detail-report', detailConfig.detailReportCode, linkValue],
    queryFn: async () => {
      if (!sourceId || linkValue == null) return [];
      const res = await queryDataSource({
        sourceId,
        parameters: { [detailConfig.linkField]: String(linkValue) },
      });
      return res.data || [];
    },
    enabled: !!sourceId && linkValue != null,
  });

  if (!detailReport) {
    return (
      <div style={styles.panel}>
        <div style={styles.title}>{detailConfig.title}</div>
        <div style={styles.warn}>Detay raporu bulunamadı: {detailConfig.detailReportCode}</div>
      </div>
    );
  }

  const headerTitle = `${detailConfig.title}${linkValue != null ? ` — ${linkValue}` : ''}`;

  if (isError) {
    return (
      <div style={styles.panel}>
        <div style={styles.title}>{headerTitle}</div>
        <div style={styles.warn}>Detay verisi yüklenemedi.</div>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.title}>{headerTitle}</div>
      <DataGrid<DetailRow>
        columns={columnDefs}
        rows={(data ?? []) as DetailRow[]}
        loading={isLoading}
        loadingRows={4}
        pageSize={20}
        showQuickFilter={false}
        enableExport={false}
        enableDensity={false}
        enableColumnPicker={false}
        enableColumnFilter={false}
        enableGrouping={false}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: { marginTop: 12, padding: 12, background: 'var(--color-card-bg)', border: '1px solid var(--color-border)', borderRadius: 4 },
  title: { fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 8 },
  warn:  { fontSize: 12, color: 'var(--color-text-muted)', padding: 8 },
};
